import { __awaiter } from "tslib";
import * as React from 'react';
import { useContext } from 'react';
import i18n from 'i18next';
import { de, enGB, kk, ru, hy, ka, tr, cs, be, zhCN, ko, hu } from 'date-fns/locale';
import { format as originalFormat, parseISO, formatDistance as originalFormatDistance } from 'date-fns';
import { eventChannel } from 'redux-saga';
import * as Sentry from '@sentry/react';
import cn from 'classnames';
import { TravellerFieldEnum, GdsServiceProductStatus, SsrStatus, Gender } from '@websky/graphql';
import { Locale } from './types';
import { Currency } from './enums';
import { getCurrency, getLocaleFromLocalStorage, setLocaleToLocalStorage } from './cache';
import memes from './memes';
import { useMediaQuery } from 'react-responsive';
import dataLayerAnalytics from './DataLayerAnalytics/DataLayerAnalytics';
import tg from './Locales/tg';
dataLayerAnalytics.init({
    currency: getCurrency()
});
export const STAY_LOGGED_IN = 'STAY_LOGGED_IN';
export const API_DATE_FORMAT = 'yyyy-MM-dd';
export const DATE_REGEXP = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const SHOW_RECAPTCHA = 'SHOW_RECAPTCHA';
export const CORPORATE_CARD = 'CORPORATE_CARD';
export const TABLET_MIN_WIDTH = 1024;
export const TABLET_MIDDLE_WIDTH = 768;
export const MOBILE_MIN_WIDTH = 600;
export const LOCALES = new Set([
    Locale.Russian,
    Locale.English,
    Locale.German,
    Locale.Kazakh,
    Locale.French,
    Locale.Chinese,
    Locale.Armenian,
    Locale.Georgian,
    Locale.Turkish,
    Locale.Tajik,
    Locale.Czech,
    Locale.Belarusian,
    Locale.Korean
]);
export const SOGAZ_INSURANCE_CODES = ['gen_trans_sogaz_avia_rw'];
export const convertICULocale = (locale) => {
    /**
     * ICU format locale for kazakh is kk, but on tickets.scat.kz but the site uses kz,
     * in this function we convert it
     */
    const localeLangMap = new Map([['kk', 'kz']]);
    if (!localeLangMap.has(locale)) {
        return locale;
    }
    else {
        return localeLangMap.get(locale);
    }
};
export const getInfo = (options = {}) => ({
    info: Object.assign({ inline: true, source: true, maxPropsIntoLine: 1, maxPropArrayLength: 2 }, options)
});
export const isDemo = document.location.href.includes('demo.websky.tech');
export const createWithThemeDecorator = (Consumer) => {
    return (Component) => {
        const NewComponent = class extends React.Component {
            render() {
                return React.createElement(Consumer, null, context => React.createElement(Component, Object.assign({}, this.props, { theme: context })));
            }
        };
        NewComponent.displayName = Component.displayName;
        return NewComponent;
    };
};
export const createTheme = (defaultTheme) => {
    const Context = React.createContext(defaultTheme);
    return {
        Context: Context,
        Consumer: Context.Consumer,
        Provider: Context.Provider,
        withTheme: createWithThemeDecorator(Context.Consumer),
        useTheme: () => useContext(Context)
    };
};
export const sum = (...money) => {
    let result = { amount: 0, currency: Currency.USD };
    money.forEach(obj => {
        if (result.amount !== 0) {
            result.amount += obj.amount;
        }
        else {
            result = Object.assign({}, obj);
        }
    });
    return result;
};
let currentLocale = getLocaleFromLocalStorage();
const syncCurrentLocaleWithI18n = () => {
    const updateLocaleIfNeeded = (lng) => {
        if (lng && lng !== currentLocale) {
            currentLocale = lng;
        }
    };
    if (i18n.isInitialized) {
        updateLocaleIfNeeded(i18n.language);
    }
    else {
        i18n.on('initialized', ({ lng }) => {
            updateLocaleIfNeeded(lng);
        });
    }
};
syncCurrentLocaleWithI18n();
// const defaultResources: i18n.Resource = {};
export let currentCurrency = getCurrency();
// No cached locale - pull from local storage.
if (!currentCurrency) {
    currentCurrency = getCurrency();
}
export const getLocale = () => {
    if (!i18n.isInitialized) {
        const lsLocale = getLocaleFromLocalStorage();
        return lsLocale ? lsLocale : Locale.Russian;
    }
    return i18n.language;
};
const changeLanguage = (locale) => __awaiter(void 0, void 0, void 0, function* () {
    setLocaleToLocalStorage(locale);
    currentLocale = locale;
    return i18n.changeLanguage(locale);
});
export const changeLocale = (locale) => __awaiter(void 0, void 0, void 0, function* () {
    if (LOCALES.has(locale)) {
        if (i18n.isInitialized) {
            yield changeLanguage(locale);
        }
        else {
            i18n.on('initialized', () => __awaiter(void 0, void 0, void 0, function* () {
                yield changeLanguage(locale);
            }));
        }
    }
});
export const extendI18n = memes((newResources) => {
    if (!i18n.isInitialized) {
        i18n.on('initialized', () => {
            addResourcesToI18n(newResources);
        });
    }
    else {
        addResourcesToI18n(newResources);
    }
});
const addResourcesToI18n = (newResources) => {
    for (const locale in newResources) {
        if (newResources.hasOwnProperty(locale)) {
            for (const moduleName in newResources[locale]) {
                if (newResources[locale].hasOwnProperty(moduleName)) {
                    i18n.addResourceBundle(locale, moduleName, newResources[locale][moduleName], true, true);
                    console.log('Resource bundle added:', locale, moduleName);
                }
            }
        }
    }
};
let userLocation;
export const getUserLocation = (onSuccess, onError) => {
    if (userLocation) {
        onSuccess(userLocation);
    }
    else if (navigator && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(({ coords }) => {
            if (coords.longitude && coords.latitude) {
                userLocation = {
                    longitude: coords.longitude.toString(),
                    latitude: coords.latitude.toString()
                };
                onSuccess(userLocation);
            }
        }, error => onError && onError(error.message));
    }
    else if (onError) {
        onError('GeoLocation API is not available for that kind of browsers');
    }
};
export const format = (date, format, options) => {
    let resultFormattedDate = '';
    let localeObject;
    switch (currentLocale) {
        case Locale.Russian:
            localeObject = ru;
            break;
        case Locale.English:
            localeObject = enGB;
            break;
        case Locale.Tajik:
            localeObject = tg;
            break;
        case Locale.Georgian:
            localeObject = ka;
            break;
        case Locale.Armenian:
            localeObject = hy;
            break;
        case Locale.German:
            localeObject = de;
            break;
        case Locale.Kazakh:
            localeObject = kk;
            break;
        case Locale.Turkish:
            localeObject = tr;
            break;
        case Locale.Czech:
            localeObject = cs;
            break;
        case Locale.Belarusian:
            localeObject = be;
            break;
        case Locale.Chinese:
            localeObject = zhCN;
            break;
        case Locale.Korean:
            localeObject = ko;
            break;
        case Locale.Hungarian:
            localeObject = hu;
            break;
    }
    try {
        const parsedDate = typeof date === 'string' ? parseISO(date) : date;
        const formattedDate = originalFormat(parsedDate, format, Object.assign(Object.assign({}, options), { locale: localeObject }));
        resultFormattedDate = formattedDate.endsWith('.')
            ? formattedDate.substr(0, formattedDate.length - 1)
            : formattedDate;
    }
    catch (error) {
        console.error(`Date formatting error: date = ${date}\n`, error);
    }
    return resultFormattedDate;
};
export const formatDistance = (date, baseDate, options) => {
    let localeObject;
    switch (currentLocale) {
        case Locale.Russian:
            localeObject = ru;
            break;
        case Locale.English:
            localeObject = enGB;
            break;
        case Locale.Georgian:
            localeObject = ka;
            break;
        case Locale.Armenian:
            localeObject = hy;
            break;
        case Locale.German:
            localeObject = de;
            break;
        case Locale.Kazakh:
            localeObject = kk;
            break;
        case Locale.Turkish:
            localeObject = tr;
            break;
        case Locale.Czech:
            localeObject = cs;
            break;
        case Locale.Belarusian:
            localeObject = be;
            break;
        case Locale.Hungarian:
            localeObject = hu;
            break;
    }
    return originalFormatDistance(date, baseDate, Object.assign(Object.assign({}, options), { locale: localeObject }));
};
export const removeDotsFromDate = (dateString) => {
    return dateString.replace('.', '');
};
export const debounce = (func, wait) => {
    let timeout;
    return function (...newArgs) {
        const later = function () {
            timeout = null;
            func(...newArgs);
        };
        clearTimeout(timeout);
        timeout = window.setTimeout(later, wait);
    };
};
/**
 * Объединяет объекты CSS-классов, склеивая значения одинаковых ключей
 * с помощью `classnames`.
 *
 * Используется для мерджа дефолтных классов из темы и пользовательских
 * переопределений (например, из props).
 *
 *
 * Поведение:
 *
 * - Если `base` и `override` отсутствуют — возвращает пустой объект.
 *
 * - Если передан только `base` — возвращает его поверхностную копию.
 *
 * - Если передан только `override` — возвращает его поверхностную копию.
 *
 * - Если переданы оба объекта:
 *   - берётся объединение ключей из `base` и `override`;
 *   - для каждого ключа значения объединяются через `classnames`;
 *   - значение из `base` всегда идёт первым,
 *     значение из `override` — вторым.
 *
 *
 * Гарантии функции:
 *
 * - входные объекты не мутируются;
 *
 * - всегда возвращается новый объект;
 *
 * - корректно обрабатываются `null`, `undefined`,
 *   пустые строки и отсутствующие ключи.
 *
 *
 * @typeParam A
 * Тип объекта базовых классов.
 *
 * @typeParam B
 * Тип объекта переопределяемых классов.
 *
 *
 * @param base
 * Базовый объект классов (например, из темы).
 *
 * @param override
 * Объект классов для переопределения (например, из props).
 *
 *
 * @returns
 * Новый объект классов, содержащий объединение ключей `base` и `override`.
 *
 *
 * @example
 * ```ts
 * mergeClasses(
 *   { button: 'btn', icon: 'icon' },
 *   { button: 'btn_primary' }
 * );
 * // => { button: 'btn btn_primary', icon: 'icon' }
 * ```
 */
export const mergeClasses = (base, override) => {
    // если ничего нет — возвращаем пустой объект
    if (!base && !override) {
        return {};
    }
    // если есть только base — возвращаем его копию
    if (base && !override) {
        return Object.assign({}, base);
    }
    // если есть только override — возвращаем его копию
    if (!base && override) {
        return Object.assign({}, override);
    }
    // иначе мерджим по объединению ключей (base и override)
    const keys = new Set([...Object.keys(base), ...Object.keys(override)]);
    const result = {};
    keys.forEach(key => {
        result[key] = cn(base[key], override[key]);
    });
    return result;
};
export const mergeCSS = (originalTheme, newTheme) => {
    const result = JSON.parse(JSON.stringify(originalTheme));
    const mergeRecursive = (target, source) => {
        for (const key in source) {
            if (!source.hasOwnProperty(key) || !target.hasOwnProperty(key)) {
                continue;
            }
            const sourceValue = source[key];
            const targetValue = target[key];
            if (typeof sourceValue === 'string' && typeof targetValue === 'string') {
                target[key] = `${targetValue} ${sourceValue}`;
            }
            else if (typeof sourceValue === 'object' &&
                typeof targetValue === 'object' &&
                sourceValue !== null &&
                targetValue !== null &&
                !Array.isArray(sourceValue) &&
                !Array.isArray(targetValue)) {
                mergeRecursive(targetValue, sourceValue);
            }
        }
    };
    mergeRecursive(result, newTheme);
    return result;
};
export const decapitalizeFirstLetter = memes((string) => {
    return string.charAt(0).toLowerCase() + string.substr(1);
});
const capitalizeFirstLetterSplittedBy = (string, separator) => {
    if (string.includes(separator)) {
        return string
            .split(separator)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(separator);
    }
    return string;
};
export const capitalizeFirstLetter = memes((string) => {
    if (!string) {
        return string;
    }
    const lowerString = string.toLowerCase();
    let newString = lowerString.charAt(0).toUpperCase() + lowerString.slice(1);
    newString = capitalizeFirstLetterSplittedBy(newString, ' ');
    return newString;
});
export const capitalizeFirstLetterInName = memes((string) => {
    const newString = capitalizeFirstLetter(string);
    return capitalizeFirstLetterSplittedBy(newString, '-');
});
export const base64ToBlob = (base64, mimeType) => {
    const binaryString = window.atob(base64);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
};
export const downloadFile = (base64, name, mimeType) => {
    const blob = base64ToBlob(base64, mimeType);
    const url = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.href = url;
    element.download = name;
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
};
export const downloadPDF = (base64, name) => {
    downloadFile(base64, name, 'application/pdf');
};
export const downloadPkpass = (base64, name) => {
    downloadFile(base64, name, 'application/vnd.apple.pkpass');
};
export const enableSentry = () => {
    if (process.env.NODE_ENV === 'production' && process.env.SENTRY_SELF_HOSTED_DSN) {
        Sentry.init({
            dsn: process.env.SENTRY_SELF_HOSTED_DSN,
            // Performance Monitoring
            tracesSampleRate: 0,
            beforeSend(event) {
                // Check if the URL fragment exists
                const locationHash = window.location.hash;
                if (locationHash) {
                    // Add the URL fragment as a tag
                    event.tags = Object.assign(Object.assign({}, event.tags), { locationHash: locationHash });
                }
                return event;
            },
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            allowUrls: [
                'https://static.flyredwings.com/',
                'https://cdn.websky.aero/',
                'https://cdn-eu1.websky.aero/',
                'https://cdn.eu2.websky.tech/',
                'https://cdn.web.websky.tech/',
                'https://static.belavia.by',
                'https://cdn.webskyx.com'
            ]
        });
    }
};
export const captureEventSentry = (event) => {
    if (process.env.NODE_ENV === 'production' && process.env.SENTRY_SELF_HOSTED_DSN) {
        Sentry.captureMessage(event.detail.message, 'error');
    }
};
export const captureErrorSentry = (message, level = 'error') => {
    if (process.env.NODE_ENV === 'production' && process.env.SENTRY_SELF_HOSTED_DSN) {
        Sentry.captureMessage(message, level);
    }
};
export const removeTrailingSlash = (string) => {
    const stringLength = string.length;
    return string.charAt(stringLength - 1) === '/' ? string.substr(0, stringLength - 1) : string;
};
export const setsAreEqual = (a, b) => {
    if (a.size !== b.size) {
        return false;
    }
    for (const index of a) {
        if (!b.has(index)) {
            return false;
        }
    }
    return true;
};
export const isOdd = (int) => (typeof int === 'string' ? parseInt(int, 10) : int) % 2 === 1;
export const useIsMobile = () => {
    return useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
};
export const useIsTabletMiddle = () => {
    return useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });
};
export const useIsTablet = () => {
    return useMediaQuery({ maxWidth: TABLET_MIN_WIDTH });
};
export const useIsDesktop = () => {
    return useMediaQuery({ minWidth: TABLET_MIN_WIDTH });
};
export const scrollTo = (top) => {
    if (window.hasOwnProperty('scrollTo')) {
        window.scrollTo({
            top,
            behavior: 'smooth'
        });
    }
};
const isSeatSegmentsGuard = (segment) => {
    return (segment.hasOwnProperty('isSeatsAvailableInCheckinOnly') ||
        segment.hasOwnProperty('services') ||
        segment.hasOwnProperty('sunSide') ||
        segment.hasOwnProperty('decks'));
};
export const segmentTabFabric = (segment) => {
    if (isSeatSegmentsGuard(segment)) {
        return {
            id: segment.segmentInfo.id,
            title: `${segment.segmentInfo.departure.airport.city.name} — ${segment.segmentInfo.arrival.airport.city.name}`
        };
    }
    return {
        id: segment.id,
        title: `${segment.departure.airport.city.name} — ${segment.arrival.airport.city.name}`
    };
};
export const getUserField = (user, type) => {
    var _a;
    return (_a = user.values) === null || _a === void 0 ? void 0 : _a.find(value => value.type === type);
};
export const getUserValue = (user, type) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = user.values) === null || _a === void 0 ? void 0 : _a.find(value => value.type === type)) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '';
};
export const getPassengerNames = (passenger) => {
    const firstName = getUserValue(passenger, TravellerFieldEnum.FirstName);
    const lastName = getUserValue(passenger, TravellerFieldEnum.LastName);
    const middleName = getUserValue(passenger, TravellerFieldEnum.MiddleName);
    return { firstName, lastName, middleName };
};
export const getUserArray = (passengerValue) => {
    const { firstName, lastName, middleName } = passengerValue;
    return [
        { name: 'firstName', value: firstName || '' },
        { name: 'lastName', value: lastName || '' },
        { name: 'middleName', value: middleName || '' }
    ];
};
/**
 * Формирует массив строк по шаблону `pattern`, сопоставляя его с данными пользователя.
 * Если токен в шаблоне равен `lastName`, `firstName` или `middleName`, результат будет приведён к виду с заглавной буквы.
 *
 * @param values Массив значений пользователя с полями `name` и `value`.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Массив значений, соответствующих шаблону.
 */
export const getPassengerLabelAsArray = (values, pattern) => {
    // Безопасное получение паттерна с fallback
    let finalPattern = pattern;
    if (!finalPattern) {
        if (i18n.isInitialized && i18n.exists('fullPassengerNamePattern')) {
            finalPattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian });
        }
        else {
            finalPattern = 'lastName firstName middleName';
        }
    }
    const tokens = finalPattern.split(' ');
    const resultValues = [];
    tokens.forEach(token => {
        var _a, _b;
        const value = (_b = (_a = values === null || values === void 0 ? void 0 : values.find(value => (value === null || value === void 0 ? void 0 : value.name) === token)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
        if (value && (token === 'lastName' || token === 'firstName' || token === 'middleName')) {
            resultValues.push(capitalizeFirstLetterInName(value.trim()));
        }
        else if (value) {
            resultValues.push(value);
        }
    });
    return resultValues;
};
/**
 * Формирует строку имени пассажира по заданному шаблону `pattern`.
 * Использует данные `firstName`, `lastName` и `middleName`.
 *
 * @param passengerValue Объект с данными пассажира.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Строка, собранная из переданных данных в порядке, заданном шаблоном.
 */
export const concatPassengerNames = (passengerValue, pattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian })) => {
    const userArray = getUserArray(passengerValue);
    return getPassengerLabelAsArray(userArray, pattern).join(' ');
};
/**
 * Возвращает строку с инициалами пассажира (например: "ИП" для Иван Петров).
 *
 * @param passengerValue Объект с данными пассажира.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Строка с первыми буквами каждого значения в шаблоне.
 */
export const getFirstCharName = (passengerValue, pattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian })) => {
    const userArray = getUserArray(passengerValue);
    return getPassengerLabelAsArray(userArray, pattern)
        .map(value => (value || '').trim()[0])
        .join('');
};
/**
 * Возвращает аватар пассажира в виде инициалов (например, "ИП") или числового ID + 1, если инициалы не найдены.
 *
 * @param passengerState Состояние пассажира, содержащее `id`, `firstName`, `lastName` и т.д.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Строка-аватар: инициалы или число.
 */
export const getPassengerAvatar = (passengerState, pattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian })) => {
    const { firstName, lastName } = getPassengerNames(passengerState);
    const passengerInitials = getFirstCharName({ lastName, firstName }, pattern);
    return passengerInitials ? passengerInitials : (parseInt(passengerState.id) + 1).toString();
};
/**
 * Возвращает строку с фамилией и именем пассажира по заданному шаблону.
 * Используются только поля `lastName` и `firstName`.
 *
 * @param passenger Объект пользователя с массивом значений.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale passengerNamePattern = 'lastName firstName'.
 * @returns Строка в формате "Фамилия Имя".
 */
export const getTravellerName = (passenger, pattern = i18n.t('passengerNamePattern', { fallbackLng: Locale.Russian })) => {
    return getPassengerLabelAsArray(passenger.values || [], pattern).join(' ');
};
/**
 * Возвращает полное ФИО пассажира (фамилия, имя, отчество) по шаблону.
 *
 * @param passenger Объект пользователя с массивом значений.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Строка в формате "Фамилия Имя Отчество".
 */
export const getTravellerFullName = (passenger, pattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian })) => {
    return getPassengerLabelAsArray((passenger === null || passenger === void 0 ? void 0 : passenger.values) || [], pattern).join(' ');
};
export const getPassengerTypeLabel = (type, supplierType, count) => {
    const options = { count };
    if (supplierType && i18n.exists(`PassengerTypes:${supplierType}`)) {
        return i18n.t(`PassengerTypes:${supplierType}`, options);
    }
    return i18n.t(`PassengerTypes:${type}`, options);
};
export const getPassengerLabel = (passenger) => {
    const { firstName, lastName, middleName } = getPassengerNames(passenger);
    if (firstName && lastName) {
        return concatPassengerNames({ lastName, firstName, middleName });
    }
    return getPassengerTypeLabel(passenger.type, passenger.supplierTravellerType);
};
export const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const seatNumberRegex = /[0-9]{1,4}[A-Z]/;
/**
 * Create a bubbling CustomEvent and dispatch on target.
 *
 * @param target HTMLElement
 * @param event
 */
export const emitEvent = (target, event) => {
    try {
        target.dispatchEvent(new CustomEvent(event.type, {
            detail: event.data,
            bubbles: true
        }));
    }
    catch (e) {
        console.warn('Cannot to emit event ' + event.type);
    }
};
export const isRequireCurrencyParam = () => {
    var _a, _b, _c;
    return ['A9', 'RM', '5G', '2S', 'JI', 'DV', 'B2', 'WZ', 'SZ'].includes((_c = (_b = (_a = window.__webskyEngineConfig) === null || _a === void 0 ? void 0 : _a.global) === null || _b === void 0 ? void 0 : _b.companyInfo) === null || _c === void 0 ? void 0 : _c.iataCode);
};
export const getTravellersMap = memes((travellers) => {
    const map = new Map(), travellerTypesCount = new Map();
    travellers.forEach(traveller => {
        var _a, _b, _c, _d;
        const firstName = (_b = (_a = traveller === null || traveller === void 0 ? void 0 : traveller.values) === null || _a === void 0 ? void 0 : _a.find(value => value.type === TravellerFieldEnum.FirstName)) === null || _b === void 0 ? void 0 : _b.value, lastName = (_d = (_c = traveller === null || traveller === void 0 ? void 0 : traveller.values) === null || _c === void 0 ? void 0 : _c.find(value => value.type === TravellerFieldEnum.LastName)) === null || _d === void 0 ? void 0 : _d.value;
        const fullName = lastName && firstName ? concatPassengerNames({ lastName, firstName }) : null;
        const typeNumber = (travellerTypesCount.get(traveller.type) || 0) + 1;
        map.set(traveller.id, {
            id: traveller.id,
            name: fullName,
            type: traveller.type,
            supplierTravellerType: traveller.supplierTravellerType,
            typeNumber: typeNumber
        });
        travellerTypesCount.set(traveller.type, typeNumber);
    });
    return map;
});
export const getTotalPrice = (items, getItemPrice) => {
    const totalPrice = {
        amount: 0,
        currency: getCurrency()
    };
    if (!!(items === null || items === void 0 ? void 0 : items.length)) {
        return items.reduce((acc, item) => {
            var _a;
            const itemPrice = getItemPrice(item);
            return {
                amount: ((itemPrice === null || itemPrice === void 0 ? void 0 : itemPrice.amount) || 0) + acc.amount,
                currency: (_a = itemPrice === null || itemPrice === void 0 ? void 0 : itemPrice.currency) !== null && _a !== void 0 ? _a : acc.currency
            };
        }, totalPrice);
    }
    return totalPrice;
};
export const getCheckInUrl = (checkInURL, orderId, ticket, lastName, reserveEngines) => {
    const formattedCheckInURL = removeTrailingSlash(checkInURL);
    if ((reserveEngines === null || reserveEngines === void 0 ? void 0 : reserveEngines.redirectToWebsky1) && (reserveEngines === null || reserveEngines === void 0 ? void 0 : reserveEngines.checkin)) {
        const checkinURL = removeTrailingSlash(reserveEngines.checkin);
        const locale = convertICULocale(getLocale());
        const url = new URL(checkinURL);
        url.searchParams.set('lang', locale);
        url.searchParams.set('ticketNumber', ticket);
        url.searchParams.set('lastName', lastName);
        url.searchParams.set('submit', '');
        url.hash = 'search';
        return url.toString();
    }
    else if (lastName && ticket) {
        return `${formattedCheckInURL}#/search/${ticket}/${lastName}`;
    }
    return `${formattedCheckInURL}#/${orderId}`;
};
const variationMap = new Map([
    [[1], 0],
    [[2, 3, 4], 1],
    [[0, 5, 6, 7, 8, 9], 2]
]);
export const getI18nCountVariation = (count, variation = variationMap) => {
    var _a, _b;
    const locale = getLocale();
    if (locale === 'ru') {
        const actualCount = count >= 10 && count <= 20 ? 0 : count % 10;
        return (_b = (_a = [...variation.entries()].find(([numbers]) => numbers.includes(actualCount))) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : 0;
    }
    return count === 0 ? 0 : 2;
};
export const getMinPrice = (items, getItemPrice) => {
    if (!!items.length) {
        return items.reduce((minPrice, item) => {
            var _a;
            const price = getItemPrice(item);
            return {
                amount: price.amount < minPrice.amount ? price.amount : minPrice.amount,
                currency: (_a = price === null || price === void 0 ? void 0 : price.currency) !== null && _a !== void 0 ? _a : minPrice.currency
            };
        }, {
            amount: Infinity,
            currency: getCurrency()
        });
    }
    else {
        return {
            amount: 0,
            currency: getCurrency()
        };
    }
};
export const isUnconfirmedService = (status) => {
    return [
        GdsServiceProductStatus.Rejected,
        GdsServiceProductStatus.Canceled,
        GdsServiceProductStatus.Problematic
    ].includes(status);
};
export const isRequestedService = (status) => {
    return status === GdsServiceProductStatus.Requested;
};
export function getFlatArraySet(values) {
    return new Set(values.reduce((result, array) => [...result, ...array], []));
}
export function getSegmentGroupsMap(segmentGroups) {
    const segmentsMap = new Map();
    segmentGroups.forEach(segment => {
        segment.segments.forEach(({ id }) => {
            segmentsMap.set(id, segment.segments);
        });
    });
    return segmentsMap;
}
export const getHasRequestedServices = (travellers, ssrData) => {
    return travellers.some(traveller => {
        var _a, _b, _c, _d, _e;
        const hasRequestedServices = (_c = (_b = (_a = traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.some(service => { var _a; return (_a = service.products) === null || _a === void 0 ? void 0 : _a.some(product => (product === null || product === void 0 ? void 0 : product.status) === GdsServiceProductStatus.Requested); });
        const hasRequestedSeats = (_e = (_d = traveller.services) === null || _d === void 0 ? void 0 : _d.seats) === null || _e === void 0 ? void 0 : _e.some(service => { var _a; return ((_a = service.product) === null || _a === void 0 ? void 0 : _a.status) === GdsServiceProductStatus.Requested || service.onRequest; });
        const hasRequestedSsrData = ssrData === null || ssrData === void 0 ? void 0 : ssrData.some(ssrData => ssrData.status === SsrStatus.OnRequest);
        return hasRequestedServices || hasRequestedSeats || hasRequestedSsrData;
    });
};
// TODO: remove this when php is fixed
export const convertGenderToCorrectValue = (gender) => {
    var _a;
    const genderMap = {
        M: Gender.Male,
        F: Gender.Female,
        O: Gender.Other
    };
    return (_a = genderMap[gender]) !== null && _a !== void 0 ? _a : gender;
};
export const isFlightFareM2 = (flight) => {
    return (flight === null || flight === void 0 ? void 0 : flight.__typename) === 'FlightFareM2';
};
export const isIncludePricesGroupM2Flight = (priceGroups) => {
    if (!priceGroups || !(priceGroups === null || priceGroups === void 0 ? void 0 : priceGroups.length)) {
        return false;
    }
    return priceGroups.some(group => {
        var _a, _b, _c, _d;
        const flight = (_d = (_c = (_b = (_a = group.prices) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.flight) === null || _c === void 0 ? void 0 : _c.fares) === null || _d === void 0 ? void 0 : _d[0];
        return isFlightFareM2(flight);
    });
};
export const isExistI18nTranslate = (key, options) => {
    var _a;
    if (((_a = i18n.options) === null || _a === void 0 ? void 0 : _a.postProcess) &&
        Array.isArray(i18n.options.postProcess) &&
        i18n.options.postProcess.includes('showKeys')) {
        return true;
    }
    return !!i18n.t(key, options) && i18n.exists(key, options);
};
export const getOffsetTop = (elem) => {
    let offsetTop = 0;
    let currentElement = elem;
    while (currentElement) {
        if (!isNaN(currentElement.offsetTop)) {
            offsetTop += currentElement.offsetTop;
        }
        currentElement = currentElement.offsetParent;
    }
    return offsetTop;
};
export const getRedirectToOrderUrl = (engineURL, orderId) => {
    return `${engineURL}#/booking/${orderId}`;
};
export const redirectToOrder = (engineURL, orderId, modifyUrlFn) => {
    const redirectToOrderURL = getRedirectToOrderUrl(engineURL, orderId);
    window.open((modifyUrlFn === null || modifyUrlFn === void 0 ? void 0 : modifyUrlFn(redirectToOrderURL)) || redirectToOrderURL, '_blank');
};
export const getSelectedInsurancesByTypes = (selectedInsurances = [], insuranceTypes) => {
    if (!selectedInsurances.length) {
        return [];
    }
    const selectedInsuranceByCode = new Map();
    selectedInsurances.forEach(insurance => {
        if (insuranceTypes.includes(insurance.insuranceProgram.type)) {
            selectedInsuranceByCode.set(insurance.insuranceProgram.code, insurance.insuranceProgram);
        }
    });
    return [...selectedInsuranceByCode.values()];
};
export const getAllSegmentsServicePrice = (service) => (Object.assign(Object.assign({}, service.price), { amount: service.price.amount * service.allowedSegments.length }));
export const getAppleWalletIconUrl = () => {
    const currentLocale = getLocale();
    return {
        src: `https://cdn.websky.aero/content/frontend/images/apple-wallet/${currentLocale}/badge.svg`,
        fallbackSrc: `https://cdn.websky.aero/content/frontend/images/apple-wallet/en/badge.svg`
    };
};
export const getCroppedError = (error, subString = 'GraphQL error: ') => error ? error.replace(subString, '') : '';
export function createExternalSagaActionChannel(eventName) {
    return eventChannel(emit => {
        const listener = (event) => {
            emit(event.detail);
        };
        window.addEventListener(eventName, listener);
        return () => window.removeEventListener(eventName, listener);
    });
}
export function getLinkedTravellersMap(travellers) {
    const linkedTravellersMap = new Map();
    travellers.forEach(traveller => {
        var _a;
        if (traveller.id && ((_a = traveller.linkedTraveller) === null || _a === void 0 ? void 0 : _a.id)) {
            linkedTravellersMap.set(traveller.linkedTraveller.id, traveller.id);
        }
    });
    return linkedTravellersMap;
}
export const getStringsHasChanges = (a, b) => {
    if (a.length !== b.length) {
        return true;
    }
    return JSON.stringify(a) !== JSON.stringify(b);
};
export function getIsLoadingServiceByParams(loadingServicesTypesState, params, loadingParamKeys = ['subType', 'type']) {
    if (!params) {
        return false;
    }
    return loadingParamKeys.some(paramKey => {
        const paramValue = params[paramKey];
        if (!paramValue) {
            return false;
        }
        const loadingServicesValuesByParam = loadingServicesTypesState[paramKey];
        if (!(loadingServicesValuesByParam === null || loadingServicesValuesByParam === void 0 ? void 0 : loadingServicesValuesByParam.length)) {
            return false;
        }
        return loadingServicesValuesByParam.includes(paramValue);
    });
}
export const isImageValid = (src) => {
    return new Promise(resolve => {
        const img = document.createElement('img');
        img.onerror = () => resolve(false);
        img.onload = () => resolve(true);
        img.src = src;
    });
};
export const isAbsoluteUrl = (url) => {
    if (!url) {
        return false;
    }
    try {
        new URL(url);
        return true;
    }
    catch (_a) {
        return false;
    }
};
export const hasIntersection = (arr1, arr2) => {
    const set1 = new Set(arr1);
    for (const item of arr2) {
        if (set1.has(item)) {
            return true;
        }
    }
    return false;
};
/** Функция снимаем фокус с активного элемента (если есть) */
export const blurActiveElement = () => {
    var _a, _b;
    if (typeof document !== 'undefined') {
        (_b = (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
};
export const disableAutocompleteProps = {
    autoComplete: 'one-time-code',
    'data-bwignore': 'true',
    'data-1p-ignore': 'true',
    'data-lpignore': 'true'
};
export const getSeatMapUniqueAvailableSegments = (segments) => {
    const uniqueAvailableSegments = new Set();
    segments.forEach(segment => {
        if (segment.availableSeatsCount) {
            uniqueAvailableSegments.add(segment.segmentsInfo.id);
        }
    });
    return [...uniqueAvailableSegments];
};
